<template>
  <div
    v-if="catalogPage"
    class="catalog-page-view"
  >
    <page-header :breadcrumbs="catalogPageBreadcrumbs">
      <template v-slot:topbar>
        <div class="catalog-page-header">
          <h1>{{ catalogPage.title }}</h1>
          <action-button
            v-if="showActionButton"
            :addon-status="catalogPageStatus"
            :catalog-page="catalogPage"
            dark
            @handle-action-button="handleActionButton(catalogPage)"
          >
          </action-button>
        </div>
      </template>
    </page-header>
    <div class="page-hero-wrapper">
      <img
        class="page-hero"
        :src="getAttachmentUrl(catalogPage.heroImageId)"
      >
    </div>
    <div class="page-content-wrapper">
      <img
        class="page-icon"
        :src="getAttachmentUrl(catalogPage.iconId)"
      >
      <div class="title-wrapper">
        <h2>{{ catalogPage.title }}</h2>
        <action-button
          v-if="showActionButton"
          :addon-status="catalogPageStatus"
          :catalog-page="catalogPage"
          dark
          large
          @handle-action-button="handleActionButton(catalogPage)"
        >
        </action-button>
      </div>
      <div
        class="page-description"
      >
        {{ stripHtml(catalogPage.description) }}
      </div>
      <marquees
        :catalog-page="catalogPage"
        :marquees="sortedMarquees"
      ></marquees>
      <div class="page-stats">
        <div class="stat rating-stat">
          <p>Rating</p>
          <span>{{ catalogPage.rating.toFixed(1) }}</span>
          <star-rating
            :total-stars="5"
            :rating="catalogPage.rating"
          />
        </div>
        <div
          v-for="social in socialProof"
          :key="social.id"
          class="stat"
        >
          <p>{{ social.displayName }}</p>
          <span>{{ social.value }}</span>
        </div>
      </div>
      <reviews
        :catalog-page="catalogPage"
        :reviews="sortedReviews"
      ></reviews>
      <div class="add-catalog-bar">
        <action-button
          v-if="showActionButton"
          :addon-status="catalogPageStatus"
          :catalog-page="catalogPage"
          dark
          large
          @handle-action-button="handleActionButton(catalogPage)"
        >
        </action-button>
      </div>
    </div>
    <catalog-add-modal />
    <catalog-remove-modal />
  </div>
</template>

<script>
import ActionButton from '@/components/catalog/action-button.vue';
import CatalogAddModal from '@/components/catalog/add-modal.vue';
import CatalogRemoveModal from '@/components/catalog/remove-modal.vue';
import Marquees from '@/components/catalog/marquees.vue';
import PageHeader from '@/components/singletons/pageHeader.vue';
import Reviews from '@/components/catalog/reviews.vue';
import StarRating from '@/components/catalog/star-rating.vue';

import CatalogPage from '@/classes/catalog-page';

import { stripHtml } from '@/utils';

export default {
  name: 'CatalogShowView',
  components: {
    ActionButton,
    CatalogAddModal,
    CatalogRemoveModal,
    Marquees,
    PageHeader,
    Reviews,
    StarRating,
  },
  computed: {
    catalogPage() {
      const catalogPageId = this.$route.params.catalogPageId;
      return this.$store.getters['catalog/pages'][catalogPageId];
    },
    catalogPageBreadcrumbs() {
      return [
        { name: 'Catalog', route: { name: 'catalog#index' } },
        { name: this.catalogPage?.title, route: { name: 'catalog#page#show' } },
      ];
    },
    catalogPageStatus() {
      return this.catalogPage.catalogPageStatus(this.$store);
    },
    showActionButton() {
      const user = this.$store.getters['user/get'];
      if (user?.previewer) {
        return true;
      }
      if (!this.catalogPage.purchaseEnabled) {
        return false;
      }
      const pageStatus = this.catalogPage.catalogPageStatus(this.$store);
      return pageStatus !== CatalogPage.CATALOG_PAGE_STATUSES.ACTIVE;
    },
    sortedMarquees() {
      return [...this.catalogPage?.marquees || []].sort((a, b) => a.order > b.order ? 1 : -1);
    },
    sortedReviews() {
      return [...this.catalogPage?.reviews || []].sort((a, b) => a.order > b.order ? 1 : -1);
    },
    socialProof() {
      return [...this.catalogPage?.socialProof || []].sort((a, b) => a.order > b.order ? 1 : -1);
    },
  },
  created() {
    const attachmentIds = [];
    if (this.catalogPage) {
      attachmentIds.push(this.catalogPage?.heroImageId);
      attachmentIds.push(this.catalogPage?.iconId);
      attachmentIds.push(this.catalogPage?.addConfig?.iconId);
      attachmentIds.push(this.catalogPage?.removeConfig?.iconId);
      for (const marquee of this.catalogPage?.marquees) {
        attachmentIds.push(marquee.iconId);
        attachmentIds.push(marquee.imageId);
      }
    }
    if (attachmentIds.length > 0) {
      this.$store.dispatch('attachments/fetch', { ids: attachmentIds });
    }
  },
  methods: {
    getAttachmentUrl(attachmentId) {
      return this.$store.getters['attachments/get'][attachmentId]?.url;
    },
    handleActionButton(catalogPage) {
      const source = { type: 'Catalog Page', id: catalogPage.id, name: catalogPage.title };
      if (catalogPage.isAddonActive(this.$store)) {
        this.$router.push({
          name: 'dashboard#show',
          params: {
            dashboardId: catalogPage.getDashboardId(this.$store),
          },
        });
        this.$analytics.events.viewedCatalogPage(catalogPage, {
          id: catalogPage.id,
          type: 'Catalog Page',
          name: 'Open Button',
        });
      } else {
        this.$modal.show('catalog-add-modal', { catalogPage, source });
        this.$analytics.events.selectedAddOn(catalogPage, { id: catalogPage.id, name: catalogPage.title, type: 'Catalog Show View' });
      }
    },
    stripHtml,
  },
};
</script>

<style lang="scss" scoped>
.catalog-page-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
}
.catalog-page-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: rem-calc(109);
  padding: 0 2rem 0 2rem !important;

  .page-hero-wrapper {
    height: rem-calc(274);
    width: calc(100% + 4rem);
    .page-hero {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }
  .hero-icon-wrapper {
    position: relative;
    .page-icon {
      position: absolute;
      left: rem-calc(280);
      bottom: rem-calc(-51);
      height: rem-calc(103);
      width: rem-calc(103);
    }
  }

  .page-content-wrapper {
    max-width: 48rem;
    width: 100%;
    margin-top: rem-calc(-52);

    .page-icon {
      height: rem-calc(103);
      width: rem-calc(103);
    }
    .title-wrapper {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      h2 {
        margin: 2rem 0;
        font-size: 3rem;
        font-weight: $bold;
        @include charter-font;
      }
    }

    .page-description {
      margin-bottom: 5rem;
      line-height: rem-calc(24);

      /deep/ p {
        margin: 0;
      }
    }

    .page-stats {
      display: flex;
      justify-content: space-between;
      margin-bottom: 4rem;
      .stat {
        color: #000000;
        font-weight: $bold;
        p {
          margin: 0 0 rem-calc(10) 0;
          font-size: 1rem;
        }
        span {
          @include charter-font;
          font-size: rem-calc(36);
        }
        /deep/ .catalog-star-rating-component {
          margin-left: 1rem;
        }
      }
    }
    .add-catalog-bar {
      display: none;
      width: 100%;
      padding-bottom: 2rem;
    }
  }
  @include breakpoint(small) {
    .catalog-page-header {
      /deep/ .catalog-action-button-component {
        display: none;
      }
    }
    .page-content-wrapper {
      margin-top: rem-calc(-32);

      .page-icon {
        height: rem-calc(64);
        width: rem-calc(64);
      }
      .title-wrapper {
        h2 {
          font-size: 2rem;
        }
        .catalog-action-button-component {
          display: none;
        }
      }
      .page-stats {
        justify-content: center;
        align-items: center;
        flex-direction: column;
        .stat {
          display: flex;
          flex-direction: column-reverse;
          text-align: center;
          p {
            margin: 0;
          }
          span {
            margin: 0 0 rem-calc(10) 0;
          }
          &:not(:last-child) {
            padding-bottom: 4rem;
          }
        }
        .rating-stat {
          flex-direction: column;
          p {
            display: none;
          }
        }
      }
      .add-catalog-bar {
        display: block;
        /deep/ .catalog-action-button-component .add-text {
          display: none;
        }
        /deep/ .catalog-action-button-component button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          margin: 0;
        }
      }
    }
  }
}
</style>
